/**
 * Behaviour for the server-rendered validity-check form (partials/ssr/validity-check.blade.php).
 *
 * This used to be an inline <script> inside the markup, which could never work: the root Vue app
 * compiles #app's HTML as its template, strips <script>/<style>, and re-renders — so the original
 * nodes (and every listener bound to them) are thrown away and the script never runs again. The
 * submit button then fell back to a native GET submit and the API was never called. Binding from
 * here instead runs after the re-render, against the nodes that actually stay in the DOM.
 */

import { licensePlateForApi } from '../utils/licensePlateForApi.js';

const SESSION_NOT_FOUND = 'czechvignette_validity_check_last';
const SESSION_ACTIVE = 'czechvignette_vignette_active';
const API_URL = '/api/validity/charge-registrations';

function csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

function countryLabelOf(option) {
    if (!option) return '';
    return String(option.textContent || '').replace(/\s*\([A-Za-z]{2,3}\)\s*$/i, '').trim();
}

function persistResult(key, countryCode, licensePlate, option, edalniceData) {
    const payload = {
        license_plate: licensePlate,
        country_id: countryCode,
        country_label: countryLabelOf(option),
        plate_key: countryCode,
        plate_strip_code: countryCode.slice(0, 3),
        flag_iso: countryCode.slice(0, 2).toLowerCase(),
        verified_at: new Date().toISOString(),
    };

    if (edalniceData && typeof edalniceData === 'object') {
        payload.edalnice = edalniceData;
    }

    try {
        sessionStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
        /* private mode / quota */
    }
}

function parseQuery() {
    const sp = new URLSearchParams(window.location.search);

    return {
        plate_country: String(sp.get('plate_country') || '').trim().toUpperCase(),
        license_plate: String(sp.get('license_plate') || '').trim(),
        auto: ['1', 'true', 'yes'].indexOf(String(sp.get('auto') || '').trim().toLowerCase()) !== -1,
    };
}

export function initValidityCheckForm() {
    if (typeof document === 'undefined') return;
    if (window.__validityCheckFormBound) return;

    const root = document.getElementById('validity-check-page');
    if (!root) return;

    const form = document.getElementById('vc-validity-form');
    if (!form) return;

    window.__validityCheckFormBound = true;

    const countrySelect = document.getElementById('vc-plate-country');
    const plateInput = document.getElementById('vc-license-plate');
    const hpInput = document.getElementById('vc-validity-hp');
    const submitBtn = document.getElementById('vc-submit-btn');
    const formSection = document.getElementById('vc-form-section');
    const verifyingSection = document.getElementById('vc-verifying-section');
    const errCountry = document.getElementById('vc-error-country');
    const errPlate = document.getElementById('vc-error-plate');
    const plateWrap = document.getElementById('vc-plate-wrap');

    let edlcodes = {};
    try {
        edlcodes = JSON.parse(root.getAttribute('data-edlcodes') || '{}');
    } catch (e) {
        edlcodes = {};
    }

    function selectedCountryOption() {
        if (!countrySelect) return null;
        const option = countrySelect.options[countrySelect.selectedIndex];
        return option && option.value ? option : null;
    }

    function updatePlatePlaceholder() {
        const option = selectedCountryOption();
        if (!option || !plateInput) return;
        const placeholder = option.getAttribute('data-placeholder') || '';
        if (placeholder) plateInput.placeholder = placeholder;
    }

    function clearErrors() {
        [errCountry, errPlate].forEach((el) => {
            if (!el) return;
            el.textContent = '';
            el.classList.add('hidden');
        });
        if (countrySelect) countrySelect.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
        if (plateWrap) plateWrap.classList.remove('ring-red-500', 'ring-2');
    }

    function showError(field) {
        if (field === 'country' && countrySelect) {
            countrySelect.classList.add('border-red-500', 'ring-2', 'ring-red-200');
            if (errCountry) {
                errCountry.textContent = ' ';
                errCountry.classList.remove('hidden');
            }
        }
        if (field === 'plate' && plateWrap) {
            plateWrap.classList.add('ring-red-500', 'ring-2');
            if (errPlate) {
                errPlate.textContent = ' ';
                errPlate.classList.remove('hidden');
            }
        }
    }

    function showVerifying(plate, countryLabel) {
        if (formSection) formSection.classList.add('hidden');
        if (verifyingSection) {
            verifyingSection.classList.remove('hidden');
            verifyingSection.setAttribute('aria-hidden', 'false');
        }
        const plateEl = document.getElementById('vc-verify-plate');
        const countryEl = document.getElementById('vc-verify-country');
        if (plateEl) plateEl.textContent = plate || '—';
        if (countryEl) countryEl.textContent = countryLabel || '—';
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function goTo(attribute, fallback) {
        window.location.assign(root.getAttribute(attribute) || fallback);
    }

    async function submitForm() {
        clearErrors();

        const countryCode = String(countrySelect?.value || '').trim().toUpperCase();
        const licensePlate = licensePlateForApi(plateInput?.value);
        if (plateInput) plateInput.value = licensePlate;
        const countryUuid = edlcodes[countryCode] || '';
        const option = selectedCountryOption();

        if (!countryCode || !countryUuid) showError('country');
        if (!licensePlate) showError('plate');
        if (!countryCode || !countryUuid || !licensePlate) return;

        showVerifying(licensePlate, countryLabelOf(option));
        if (submitBtn) submitBtn.disabled = true;

        try {
            const res = await fetch(API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': csrfToken(),
                    'X-Requested-With': 'XMLHttpRequest',
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    country_id: countryUuid,
                    license_plate: licensePlate,
                    validity_hp: hpInput ? hpInput.value : '',
                }),
            });
            const payload = await res.json().catch(() => ({}));

            if (payload.is_valid) {
                persistResult(SESSION_ACTIVE, countryCode, licensePlate, option, payload.data);
                goTo('data-vignette-active-url', '/vignette-active/');
                return;
            }

            persistResult(SESSION_NOT_FOUND, countryCode, licensePlate, option, null);
            goTo('data-vignette-not-found-url', '/vignette-not-found/');
        } catch (e) {
            persistResult(SESSION_NOT_FOUND, countryCode, licensePlate, option, null);
            goTo('data-vignette-not-found-url', '/vignette-not-found/');
        } finally {
            if (submitBtn) submitBtn.disabled = false;
        }
    }

    if (countrySelect) {
        countrySelect.addEventListener('change', () => {
            clearErrors();
            updatePlatePlaceholder();
        });
    }

    form.addEventListener('submit', (e) => {
        e.preventDefault();
        submitForm();
    });

    /* Deep links from How-it-works: ?plate_country=CZ&license_plate=…&auto=1 */
    const pre = parseQuery();
    if (pre.plate_country && countrySelect) {
        countrySelect.value = pre.plate_country;
        updatePlatePlaceholder();
    }
    if (pre.license_plate && plateInput) {
        plateInput.value = licensePlateForApi(pre.license_plate);
    }
    if (pre.auto && pre.plate_country && pre.license_plate) {
        submitForm();
    }
}
